import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ScreenPath } from '../../navigation'
import { dataSource } from '../../service/domain/_service.config'
import { ACTIVITY_LOGS_QUERY_KEY } from '../activity/useActivityLogs'
import { ENTITLEMENT_QUERY_KEY } from '../membership/useChallengeAccess'
import { TEAM_MEMBERSHIPS_QUERY_KEY } from '../membership/useTeamMemberships'
import { useUser } from '../user/useUser'
import { TEAM_QUERY_KEY } from './useTeam'
import { TEAMS_QUERY_KEY } from './useTeams'

export function useLeaveTeam() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: user } = useUser()

  return useMutation({
    mutationFn: async (teamID: string) => {
      if (!user) throw new Error('No user found')
      if (!teamID) throw new Error('No team to leave')

      const team = await dataSource.getTeam(teamID)
      if (!team) throw new Error('Team not found')
      if (!team.hasMember(user.id)) throw new Error('User not in this team')

      if (team.leaderID === user.id) {
        const newLeader = team.getNextLeader()
        if (newLeader) {
          await dataSource.updateTeamLeader(teamID, newLeader.id)
        }
      }

      await dataSource.removeTeamMember(user.id, teamID)
      return { teamID, challengeID: team.challengeID, userID: user.id }
    },
    onSuccess: ({ teamID: leftTeamID, challengeID, userID }) => {
      navigate(ScreenPath.home, { replace: true })
      queryClient.invalidateQueries({ queryKey: TEAMS_QUERY_KEY(challengeID) })
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY(leftTeamID) })
      queryClient.invalidateQueries({ queryKey: TEAM_MEMBERSHIPS_QUERY_KEY(userID) })
      queryClient.invalidateQueries({
        queryKey: ENTITLEMENT_QUERY_KEY(userID, challengeID),
      })
      queryClient.invalidateQueries({ queryKey: ACTIVITY_LOGS_QUERY_KEY(leftTeamID) })
    },
  })
}
